import {
  ActionType,
  AutoTextField,
  BooleanField,
  SelectField,
} from '../../../components/AutoForm/AutoFieldNew';
import {ProtectionTypeEnum} from '../../../schema-types/globalTypes';
import {builderFieldsType} from '../CreateBuilder/builderFields';

export type setUpProtectionFields = protectionFields & {
  protectionType?: ProtectionTypeEnum;
  notes?: string;
  terminatingAge?: string;
  term?: string;
};

export type protectionFields = {
  livesAssured?: string;
  premiumType?: string;
  lifeCover?: string;
  monthlyIncome?: string;
  criticalIllnessCover?: string;
  lifeOrCriticalIllnessCover?: string;
  deferredPeriod?: string;
  investmentLinked?: boolean;

  waiverOfPremium?: boolean;
  inTrust?: boolean;

  termType?: string;
};

export const protectionTypeToFieldsMatrix: Record<
  keyof protectionFields,
  Record<ProtectionTypeEnum, boolean>
> = {
  livesAssured: {
    level_term: true,
    other: true,
    decreasing_term: true,
    family_income: true,
    critical_illness: true,
    income_protection: false,
  },
  premiumType: {
    level_term: true,
    other: true,
    decreasing_term: true,
    family_income: true,
    critical_illness: true,
    income_protection: true,
  },
  lifeCover: {
    level_term: true,
    other: true,
    decreasing_term: true,
    family_income: false,
    critical_illness: false,
    income_protection: false,
  },
  monthlyIncome: {
    level_term: false,
    other: false,
    decreasing_term: false,
    family_income: true,
    critical_illness: false,
    income_protection: true,
  },
  criticalIllnessCover: {
    level_term: true,
    other: true,
    decreasing_term: true,
    family_income: false,
    critical_illness: true,
    income_protection: false,
  },
  lifeOrCriticalIllnessCover: {
    level_term: true,
    other: true,
    decreasing_term: true,
    family_income: false,
    critical_illness: false,
    income_protection: false,
  },
  deferredPeriod: {
    level_term: false,
    other: false,
    decreasing_term: false,
    family_income: false,
    critical_illness: false,
    income_protection: true,
  },
  investmentLinked: {
    level_term: false,
    other: false,
    decreasing_term: false,
    family_income: false,
    critical_illness: false,
    income_protection: false,
  },
  termType: {
    level_term: true,
    other: true,
    decreasing_term: true,
    family_income: true,
    critical_illness: true,
    income_protection: true,
  },
  waiverOfPremium: {
    level_term: true,
    other: true,
    decreasing_term: true,
    family_income: true,
    critical_illness: true,
    income_protection: true,
  },
  inTrust: {
    level_term: true,
    other: true,
    decreasing_term: true,
    family_income: true,
    critical_illness: true,
    income_protection: false,
  },
};

// protection plan
export const protectionTypeField: SelectField<builderFieldsType, ProtectionTypeEnum> = {
  id: 'protectionPlan.protectionType',
  label: 'Protection Type',
  type: ActionType.select,
  options: [
    {
      label: 'Level Term Assurance (with and without CI)',
      value: ProtectionTypeEnum.level_term,
    },
    {
      label: 'Decreasing Term Assurance (with and without CI)',
      value: ProtectionTypeEnum.decreasing_term,
    },
    {
      label: 'Family Income Benefit',
      value: ProtectionTypeEnum.family_income,
    },
    {
      label: 'Critical Illness Cover',
      value: ProtectionTypeEnum.critical_illness,
    },
    {
      label: 'Income Protection',
      value: ProtectionTypeEnum.income_protection,
    },
    {
      label: 'Other',
      value: ProtectionTypeEnum.other, // todo, change this
    },
  ],
};
export const livesAssuredField: SelectField<builderFieldsType> = {
  id: 'protectionPlan.livesAssured',
  label: 'Lives assured',
  type: ActionType.select,
  options: [], // dynamically generated
};
export const premiumTypeField: SelectField<builderFieldsType> = {
  id: 'protectionPlan.premiumType',
  label: 'Premium type',
  type: ActionType.select,
  options: [
    {
      value: 'reviewable',
      label: 'Reviewable',
    },
    {
      value: 'guaranteed',
      label: 'Guaranteed',
    },
  ],
};
export const monthlyIncomeTextField: AutoTextField<builderFieldsType> = {
  id: 'protectionPlan.monthlyIncome',
  label: 'Monthly income (£)',
  type: ActionType.text,
  currency: true,
  initialValue: '',
};
export const liveCoverField: AutoTextField<builderFieldsType> = {
  id: 'protectionPlan.lifeCover',
  label: 'Life cover (£)',
  type: ActionType.text,
  currency: true,
  initialValue: '',
};
export const criticalIllnessCoverField: AutoTextField<builderFieldsType> = {
  id: 'protectionPlan.criticalIllnessCover',
  label: 'Critical illness cover (£)',
  type: ActionType.text,
  currency: true,
  initialValue: '',
};
export const deferredPeriodField: SelectField<builderFieldsType> = {
  id: 'protectionPlan.deferredPeriod',
  label: 'Deferred period',
  type: ActionType.select,
  options: [
    {label: '0 weeks', value: '0'},
    {label: '1 week', value: '1'},
    {label: '2 weeks', value: '2'},
    {label: '4 weeks', value: '4'},
    {label: '8 weeks', value: '8'},
    {label: '13 weeks', value: '13'},
    {label: '26 weeks', value: '26'},
    {label: '52 weeks', value: '52'},
    {label: '104 weeks', value: '104'},
  ],
  initialValue: '',
};
export const investmentLinkedField: BooleanField<builderFieldsType> = {
  id: 'protectionPlan.investmentLinked',
  label: 'Investment Linked',
  type: ActionType.boolean,
  yesValue: true,
  noValue: false,
  labelNotClickable: true,
};
export const termTypeField: SelectField<builderFieldsType> = {
  id: 'protectionPlan.termType',
  label: 'Term type',
  type: ActionType.select,
  options: [
    {
      value: 'terminatingAge',
      label: 'Terminating age',
    },
    {
      value: 'years',
      label: 'Term',
    },
  ],
};

export const termField: AutoTextField<builderFieldsType> = {
  id: 'protectionPlan.term',
  label: 'Term (years)',
  type: ActionType.text,
  initialValue: '',
  number: true,
};

export const toAgeField: AutoTextField<builderFieldsType> = {
  id: 'protectionPlan.terminatingAge',
  label: 'Terminating age (years)',
  type: ActionType.text,
  initialValue: '',
  validation: (val) => {
    if (!val) {
      return 'Please choose a value';
    }

    const num = Number(val);

    if (!Number.isInteger(num)) {
      return 'Please enter a whole number';
    }

    if (num < 20 || num > 125) {
      return 'Age must be between 20 and 125';
    }

    return undefined;
  },
};

export const waiverOfPremiumField: BooleanField<builderFieldsType> = {
  id: 'protectionPlan.waiverOfPremium',
  label: 'Waiver of premium',
  type: ActionType.boolean,
  yesValue: true,
  noValue: false,
  labelNotClickable: true,
};
export const protectionInTrustField: BooleanField<builderFieldsType> = {
  id: 'protectionPlan.inTrust',
  label: 'In Trust',
  type: ActionType.boolean,
  yesValue: true,
  noValue: false,
  labelNotClickable: true,
};
export const protectionPlanNotesField: AutoTextField<builderFieldsType> = {
  id: 'protectionPlan.notes',
  label: 'Notes',
  type: ActionType.text,
  initialValue: '',
  multiline: true,
  minRows: 10,
};
